/**
 * PDF Text Extraction with SmolDocling OCR for scanned documents
 *
 * Flow:
 * 1. Try native text extraction with pdfjs-dist
 * 2. If scanned (< 100 chars), use SmolDocling via WebGPU
 */

import * as pdfjsLib from 'pdfjs-dist';
import type { PDFExtractionResult, PageContent, OCRStatus } from './types';

// Configure PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url
).toString();

// Minimum characters to consider a page as having native text
const MIN_TEXT_THRESHOLD = 100;

// SmolDocling model singleton
let smolDoclingPipeline: any = null;
let modelLoadPromise: Promise<any> | null = null;

/**
 * Check if WebGPU is available in the browser
 */
export async function checkWebGPUSupport(): Promise<boolean> {
  // WebGPU types aren't in default lib yet
  const nav = navigator as Navigator & { gpu?: { requestAdapter: () => Promise<unknown | null> } };
  if (!nav.gpu) {
    return false;
  }
  try {
    const adapter = await nav.gpu.requestAdapter();
    return adapter !== null;
  } catch {
    return false;
  }
}

/**
 * Load SmolDocling model (lazy, singleton)
 */
export async function loadSmolDoclingModel(
  onProgress?: (status: OCRStatus) => void
): Promise<any> {
  if (smolDoclingPipeline) {
    return smolDoclingPipeline;
  }

  if (modelLoadPromise) {
    return modelLoadPromise;
  }

  modelLoadPromise = (async () => {
    onProgress?.({
      stage: 'loading-model',
      progress: 0,
      message: 'Loading SmolDocling OCR model...',
      modelLoaded: false,
    });

    try {
      // Dynamic import to avoid loading transformers.js until needed
      const { pipeline, env } = await import('@huggingface/transformers');

      // Configure for WebGPU
      if (env.backends?.onnx?.wasm) {
        env.backends.onnx.wasm.numThreads = 1;
      }

      // Check WebGPU support
      const hasWebGPU = await checkWebGPUSupport();
      if (!hasWebGPU) {
        throw new Error(
          'WebGPU is not supported in this browser. Please use Chrome, Edge, or another WebGPU-enabled browser.'
        );
      }

      onProgress?.({
        stage: 'loading-model',
        progress: 30,
        message: 'Downloading SmolDocling model (256MB)...',
        modelLoaded: false,
      });

      // Load the image-to-text pipeline with SmolDocling
      // Note: Using SmolVLM as base since SmolDocling shares architecture
      smolDoclingPipeline = await pipeline(
        'image-to-text',
        'HuggingFaceTB/SmolVLM-256M-Instruct',
        {
          device: 'webgpu',
          dtype: 'fp16',
          progress_callback: (progress: any) => {
            if (progress.status === 'progress') {
              const pct = Math.round((progress.loaded / progress.total) * 100);
              onProgress?.({
                stage: 'loading-model',
                progress: 30 + pct * 0.6,
                message: `Downloading model: ${pct}%`,
                modelLoaded: false,
              });
            }
          },
        }
      );

      onProgress?.({
        stage: 'loading-model',
        progress: 100,
        message: 'Model loaded successfully',
        modelLoaded: true,
      });

      return smolDoclingPipeline;
    } catch (error) {
      modelLoadPromise = null;
      throw error;
    }
  })();

  return modelLoadPromise;
}

/**
 * Extract text from a single PDF page using native text extraction
 */
async function extractNativeText(page: any): Promise<string> {
  const textContent = await page.getTextContent();
  const strings = textContent.items.map((item: any) => item.str);
  return strings.join(' ').trim();
}

/**
 * Render a PDF page to an image for OCR
 */
async function renderPageToImage(page: any, scale: number = 2.0): Promise<HTMLCanvasElement> {
  const viewport = page.getViewport({ scale });
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d')!;

  canvas.width = viewport.width;
  canvas.height = viewport.height;

  await page.render({
    canvasContext: context,
    viewport: viewport,
  }).promise;

  return canvas;
}

/**
 * Convert canvas to blob for model input
 */
function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to convert canvas to blob'));
        }
      },
      'image/png',
      1.0
    );
  });
}

/**
 * Run OCR on an image using SmolDocling
 */
async function runOCR(
  imageBlob: Blob,
  onProgress?: (status: OCRStatus) => void
): Promise<string> {
  const model = await loadSmolDoclingModel(onProgress);

  // Convert blob to data URL for the model
  const imageUrl = URL.createObjectURL(imageBlob);

  try {
    // Use the model to extract text
    // SmolVLM uses a chat-like interface
    const result = await model(imageUrl, {
      max_new_tokens: 2048,
      do_sample: false,
    });

    // Extract text from result
    if (Array.isArray(result) && result.length > 0) {
      return result[0].generated_text || '';
    }
    return '';
  } finally {
    URL.revokeObjectURL(imageUrl);
  }
}

/**
 * Main function: Extract text from a PDF file
 */
export async function extractTextFromPDF(
  file: File,
  onProgress?: (status: OCRStatus) => void
): Promise<PDFExtractionResult> {
  onProgress?.({
    stage: 'processing',
    progress: 0,
    message: 'Loading PDF...',
    modelLoaded: !!smolDoclingPipeline,
  });

  // Load the PDF
  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  const pageCount = pdf.numPages;

  const pages: PageContent[] = [];
  let totalNativeText = '';
  let isScanned = false;

  // First pass: try native text extraction
  for (let i = 1; i <= pageCount; i++) {
    onProgress?.({
      stage: 'processing',
      progress: (i / pageCount) * 30,
      message: `Extracting text from page ${i}/${pageCount}...`,
      modelLoaded: !!smolDoclingPipeline,
    });

    const page = await pdf.getPage(i);
    const nativeText = await extractNativeText(page);

    pages.push({
      pageNumber: i,
      text: nativeText,
      hasImages: false, // Will update if we detect scanned
    });

    totalNativeText += nativeText + '\n';
  }

  // Check if this is a scanned document
  const avgCharsPerPage = totalNativeText.length / pageCount;
  isScanned = avgCharsPerPage < MIN_TEXT_THRESHOLD;

  // If scanned, run OCR
  if (isScanned) {
    onProgress?.({
      stage: 'processing',
      progress: 35,
      message: 'Scanned document detected. Loading OCR model...',
      modelLoaded: !!smolDoclingPipeline,
    });

    // Load the model first
    await loadSmolDoclingModel(onProgress);

    // OCR each page
    for (let i = 1; i <= pageCount; i++) {
      onProgress?.({
        stage: 'processing',
        progress: 35 + ((i / pageCount) * 60),
        message: `Running OCR on page ${i}/${pageCount}...`,
        modelLoaded: true,
      });

      const page = await pdf.getPage(i);
      const canvas = await renderPageToImage(page);
      const blob = await canvasToBlob(canvas);

      const ocrText = await runOCR(blob, onProgress);

      pages[i - 1] = {
        pageNumber: i,
        text: ocrText,
        hasImages: true,
      };
    }
  }

  onProgress?.({
    stage: 'complete',
    progress: 100,
    message: 'Extraction complete',
    modelLoaded: !!smolDoclingPipeline,
  });

  return {
    text: pages.map((p) => p.text).join('\n\n'),
    pageCount,
    isScanned,
    ocrUsed: isScanned,
    extractionMethod: isScanned ? 'smoldocling' : 'native',
    pages,
  };
}

/**
 * Quick check if a PDF is scanned (without full extraction)
 */
export async function isPDFScanned(file: File): Promise<boolean> {
  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

  // Check first page only for speed
  const page = await pdf.getPage(1);
  const text = await extractNativeText(page);

  return text.length < MIN_TEXT_THRESHOLD;
}

/**
 * Unload the model to free memory
 */
export function unloadSmolDoclingModel(): void {
  smolDoclingPipeline = null;
  modelLoadPromise = null;
}
